<template>
  <div id="cash-log">
    <h1>{{ $t('CASH_LOG_MANAGEMENT') }}</h1>

    <div class="summary">
      <b-row>
        <b-col xs="12" lg="3" class="cols">
          <div class="summary-item in">
            <div class="icon-holder">
              <font-awesome-icon :icon="faArrowUp" />
            </div>
            <div>{{ $t('TOTAL_CASH_IN') }}</div>
            <div class="amount">{{ addCommas(summary.totalCashIn) }}</div>
          </div>
        </b-col>

        <b-col xs="12" lg="3" class="cols">
          <div class="summary-item out">
            <div class="icon-holder">
              <font-awesome-icon :icon="faArrowUp" />
            </div>
            <div>{{ $t('TOTAL_CASH_OUT') }}</div>
            <div class="amount">{{ addCommas(summary.totalCashOut) }}</div>
          </div>
        </b-col>

        <b-col xs="12" lg="3" class="cols">
          <div class="summary-item cash">
            <div class="icon-holder">
              <font-awesome-icon :icon="faMoneyBill" />
            </div>
            <div>{{ $t('CASH_BALANCE') }}</div>
            <div class="amount">{{ addCommas(summary.cashBalance) }}</div>
          </div>
        </b-col>

        <b-col xs="12" lg="3" class="cols" v-if="isHeadAccountant">
          <div class="summary-item bank">
            <div class="icon-holder">
              <font-awesome-icon :icon="faMoneyCheck" />
            </div>
            <div>{{ $t('BANK_BALANCE') }}</div>
            <div class="amount">{{ addCommas(summary.bankBalance) }}</div>
          </div>
        </b-col>
      </b-row>
    </div>

    <div v-if="isDesktop" class="between">
      <tab-menu :tabs="tabItems" v-model="currentTab" />

      <div class="controls">
        <div class="icon-btns">
          <div class="icon-btns-wrapper">
            <search class="feature-button" />
            <primary-button
              class="feature-button"
              white
              pure-icon
              :text="$t('IMPORT_XLSX')"
              @click="browseXlsx"
            >
              <template slot="icon">
                <font-awesome-icon :icon="faFileImport" />
              </template>
            </primary-button>
            <input type="file" cloak ref="fileInputRef" @change="importXlsx" />

            <primary-button
              class="feature-button"
              white
              pure-icon
              :text="$t('EXPORT_XLSX')"
              @click="exportXlsx"
            >
              <template slot="icon">
                <font-awesome-icon :icon="faDownload" />
              </template>
            </primary-button>

            <filter-box class="feature-button" v-if="(currentTab !== TABS.CLOSURE)" />

            <date-filter
              class="feature-button"
              pure-icon
              white
              :text="$t('DATE_FILTER')"
              :presets="datePresets"
              :from="filters.fromDate"
              :to="filters.toDate"
              @filter="setDate"
            />
          </div>
        </div>

        <primary-button rounded-corner class="action-button" blue :text="$t('CLOSURE')" @click="newCashClosure" v-if="isHeadAccountant" v-show="(currentTab === TABS.CLOSURE)" />
        <primary-button rounded-corner class="action-button out" :text="`(-) ${$t('CASH_OUT')}`" @click="newCashLog(-1)" />
        <primary-button rounded-corner green class="action-button in" :text="`+ ${$t('CASH_IN')}`" @click="newCashLog(1)" />
      </div>
    </div>
    <template v-else>
      <div class="between">
        <search class="feature-button" />
        <primary-button
          class="feature-button"
          white
          pure-icon
          :text="$t('IMPORT_XLSX')"
          @click="browseXlsx"
        >
          <template slot="icon">
            <font-awesome-icon :icon="faFileImport" />
          </template>
        </primary-button>
        <input type="file" cloak ref="fileInputRef" @change="importXlsx" />

        <primary-button
          class="feature-button"
          white
          pure-icon
          :text="$t('EXPORT_XLSX')"
          @click="exportXlsx"
        >
          <template slot="icon">
            <font-awesome-icon :icon="faDownload" />
          </template>
        </primary-button>

        <filter-box class="feature-button" v-if="(currentTab !== TABS.CLOSURE)" />

        <date-filter
          class="feature-button"
          pure-icon
          white
          :text="$t('DATE_FILTER')"
          :presets="datePresets"
          :from="filters.fromDate"
          :to="filters.toDate"
          @filter="setDate"
        />
      </div>
      <br/>
      <div class="around controls">
        <primary-button rounded-corner class="action-button" blue :text="$t('CLOSURE')" @click="newCashClosure" v-if="isHeadAccountant" v-show="(currentTab === TABS.CLOSURE)" />
        <primary-button rounded-corner class="action-button out" :text="`(-) ${$t('CASH_OUT')}`" @click="newCashLog(-1)" />
        <primary-button rounded-corner green class="action-button in" :text="`+ ${$t('CASH_IN')}`" @click="newCashLog(1)" />
      </div>
      <br/>
      <tab-menu :tabs="tabItems" v-model="currentTab" />
    </template>

    <main-list :pending="(currentTab === TABS.PENDING_LIST)" ref="list" v-if="([TABS.PENDING_LIST, TABS.CASH_LOG].indexOf(currentTab) >= 0)" />
    <closure-list :pending="(currentTab === TABS.CLOSURE)" ref="closureList" v-if="currentTab == TABS.CLOSURE" />

    <editor :active="editing" :dir="editingDirection" @close="closeEditor" @submitted="closeEditor(true)" />
    <closure-editor :active="editingClosure" @close="closeEditor" @submitted="closeEditor(true)" />
  </div>
</template>

<script>
import { ref, watch, computed, onMounted } from '@vue/composition-api';
import { createNamespacedHelpers } from 'vuex-composition-helpers';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faBookmark, faExclamationCircle, faLock, faArrowUp, faMoneyCheck, faMoneyBill, faDownload, faFileImport } from '@fortawesome/free-solid-svg-icons';
import { faCalendarAlt } from '@fortawesome/free-regular-svg-icons';
import moment from 'moment';

import { PATHS, PERMISSIONS, DATE_FORMAT } from '@/consts';
import { hasPermissions } from '@/helpers/permission';
import { addCommas } from '@/helpers';

import TabMenu from '@/components/TabMenu';
import PrimaryButton from '@/components/PrimaryButton';
import DateFilter from '@/components/DateFilter';

import MainList from './MainList';
import ClosureList from './ClosureList';
import Editor from './Editor';
import ClosureEditor from './ClosureEditor';
import FilterBox from './FilterBox';
import Search from './Search';

import CashLogService from './CashLogService';

const { useState, useActions } = createNamespacedHelpers('cashLog');
const { useActions: useFlashActions } = createNamespacedHelpers('flash');
const { useState: useAppState } = createNamespacedHelpers('app');

const now = moment();

const datePresets = [
  {
    text: 'TODAY',
    range: [now.format(DATE_FORMAT), now.format(DATE_FORMAT)]
  },
  {
    text: 'THIS_MONTH',
    range: [now.clone().startOf('month').format(DATE_FORMAT), now.format(DATE_FORMAT)]
  },
  {
    text: 'LAST_MONTH',
    range: [now.clone().subtract(1, 'month').startOf('month').format(DATE_FORMAT), now.clone().subtract(1, 'month').endOf('month').format(DATE_FORMAT)]
  },
  {
    text: 'THIS_YEAR',
    range: [now.clone().startOf('year').format(DATE_FORMAT), now.format(DATE_FORMAT)]
  }
];

export const TABS = {
  CASH_LOG: 'CASH_LOG',
  PENDING_LIST: 'PENDING_LIST',
  CLOSURE: 'CLOSURE'
};

const tabItems = [
  {
    value: TABS.CASH_LOG,
    text: TABS.CASH_LOG,
    icon: faBookmark
  },
  {
    value: TABS.PENDING_LIST,
    text: TABS.PENDING_LIST,
    icon: faExclamationCircle
  },
  {
    value: TABS.CLOSURE,
    text: TABS.CLOSURE,
    icon: faLock
  }
];

export default {
  components: {
    TabMenu,
    PrimaryButton,
    MainList,
    ClosureList,
    Editor,
    ClosureEditor,
    FontAwesomeIcon,
    DateFilter,
    FilterBox,
    Search,
  },
  metaInfo() {
    const { $i18n } = this;
    const title = $i18n.t('CASH_LOG_MANAGEMENT');

    return {
      title,
      meta: []
    };
  },
  setup(props, { root: { $route, $router } }) {
    const { summary, filters } = useState(['summary', 'filters']);
    const { getSummary, applyFilter } = useActions(['getSummary', 'applyFilter']);
    const { funds, isDesktop } = useAppState(['funds', 'isDesktop']);
    const { error, info } = useFlashActions(['error', 'info']);

    const currentTab = ref($route.meta.module || TABS.CASH_LOG);
    const editing = ref(false);
    const editingClosure = ref(false);
    const editingDirection = ref(1);
    const list = ref(null);
    const closureList = ref(null);
    const fileInputRef = ref(null);

    const newCashLog = (direction) => {
      editingDirection.value = direction;
      editing.value = true;
    };

    const newCashClosure = () => {
      editingClosure.value = true;
    };

    const closeEditor = (submitted) => {
      if (submitted) {
        applyFilter({
          page: 1
        });

        if (editing.value) {
          currentTab.value = TABS.PENDING_LIST;
        }

        if (editingClosure.value) {
          currentTab.value = TABS.CLOSURE;
        }
      }

      editing.value = false;
      editingClosure.value = false;
    };

    const isHeadAccountant = computed(() => hasPermissions([PERMISSIONS.HEAD_ACCOUNTANT_ACCESS]));

    const setDate = (from, to) => {
      applyFilter({
        fromDate: from,
        toDate: to,
        page: 1
      });
    };

    const exportXlsx = async () => {
      await CashLogService.exportXlsx(filters.value);
    };

    const browseXlsx = () => {
      if (!fileInputRef.value) return;

      fileInputRef.value.click();
    };

    const importXlsx = async (e) => {
      const [file] = e.target.files;
      if (!file) return;

      const formData = new FormData();
      formData.append('file', file);

      try {
        await CashLogService.createCashLog(formData, true);
        info('FILE_IMPORT_IS_IN_PROGRESS');
      } catch(err) {
        error('FILE_IMPORT_ERROR');
      }

      fileInputRef.value.value = null;
    };

    watch(currentTab, (tab) => {
      if (tab === TABS.CASH_LOG) {
        $router.replace(PATHS.CASH_LOG);
      } else if (tab === TABS.PENDING_LIST) {
        $router.replace(PATHS.CASH_LOG_PENDING_LIST);
      } else if (tab === TABS.CLOSURE) {
        $router.replace(PATHS.CASH_LOG_CLOSURE);
      }
    });

    watch(filters, (f, lastF) => {
      const passiveFiltering = ((f.fromDate !== lastF.fromDate) || (f.toDate !== lastF.toDate) || (f.page !== lastF.page));
      if (passiveFiltering && (f.page === 1)) {
        getSummary();
      }
    });

    const checkMissingCashClosure = async () => {
      const lastDayofMonth = moment().subtract(1, 'months').endOf('month').format(DATE_FORMAT);
      const params = {
        fromDate: lastDayofMonth,
        toDate: lastDayofMonth,
      };
      const result = await CashLogService.getCashClosureList(params);
      const fundsFromCashClosure = result.items.reduce((fundKey, cashClosure) => {
        const value = { ...fundKey };
        value[cashClosure.fund.name] = true;
        return value;
      }, {});

      funds.value.some((fund) => {
        if (fundsFromCashClosure[fund.name] !== true) {
          error('CASH_CLOSURE_HAVING_PENDING_ERROR', false);
          return true;
        }
        return false;
      });
    };

    onMounted(() => {
      getSummary();
      checkMissingCashClosure();
    });

    return {
      TABS,
      tabItems,
      currentTab,
      editing,
      editingDirection,
      newCashLog,
      closeEditor,
      list,
      closureList,
      isHeadAccountant,
      newCashClosure,
      editingClosure,
      summary,
      addCommas,
      faArrowUp,
      faMoneyCheck,
      faMoneyBill,
      faCalendarAlt,
      DateFilter,
      setDate,
      filters,
      isDesktop,
      datePresets,
      exportXlsx,
      faDownload,
      faFileImport,
      fileInputRef,
      browseXlsx,
      importXlsx
    };
  }
};
</script>

<style lang="scss">
@import "~@/assets/scss/colors";

#cash-log {
  padding: 30px;

  h1 {
    font-size: 28px;
    text-transform: capitalize;
    margin-bottom: 40px;
  }

  .summary {
    margin: 0 auto;
    margin-bottom: 30px;
    max-width: 1024px;

    .row {
      margin-right: 0;
    }

    .cols {
      padding-right: 0;
    }

    .summary-item {
      display: flex;
      flex-direction: row;
      align-items: center;
      background-color: $babyPowder;
      padding: 10px 15px;
      border-radius: 4px;
      font-size: 12px;

      .icon-holder {
        border: 1px solid $independence;
        display: flex;
        align-items: center;
        border-radius: 50%;
        width: 36px;
        height: 36px;
        text-align: center;
        margin-right: 7px;

        svg {
          width: 16px;
          height: 16px;
          margin: auto;
        }
      }

      .amount {
        margin-left: auto;
        font-size: 16px;
      }

      &.in {
        color: $shamrockGreen;

        .icon-holder {
          border-color: $shamrockGreen;

          svg {
            transform: rotate(225deg);
          }
        }
      }

      &.out {
        color: $geranium;

        .icon-holder {
          border-color: $geranium;

          svg {
            transform: rotate(45deg);
          }
        }
      }

      &.bank {
        color: $independence;

        .icon-holder {
          border-color: $independence;
        }
      }

      &.cash {
        color: $blueNcs;

        .icon-holder {
          border-color: $blueNcs;
        }
      }
    }
  }

  @media (min-width: 992px) {
    .tab-menu {
      display: inline-block;
    }
  }

  .between {
    display: flex;
    justify-content: space-between;
  }

  .around {
    display: flex;
    justify-content: space-around;
  }

  .feature-button {
    border-radius: 4px;
    border: 1px solid $alto;
    box-shadow: 0px 3px 6px #00000029;
    @media (min-width: 992px) {
      margin-left: 3px;
    }

    .toggle-button {
      border-radius: 4px;
      box-shadow: 0px 0px 0px #00000029;
    }
  }

  .action-button {
    @media (min-width: 992px) {
      margin-left: 10px;
    }
    border: 2px solid $babyPowder;
    padding-left: 15px;
    padding-right: 15px;
  }

  .controls {

    .icon-btns {
      display: inline-block;
      vertical-align: middle;

      .icon-btns-wrapper {
        display: flex;
        align-items: center;
      }
    }

  }

  @media (max-width: 991px) {
    padding: 15px;

    .summary {
      .cols {
        padding-bottom: 7px;
      }
    }
  }
}
</style>
